import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { resetPassword } from '../services/registrationApi'

function validatePasswordStrength(pw: string): string {
  if (pw.length < 8)                        return 'Password must be at least 8 characters.'
  if (!/[A-Z]/.test(pw))                   return 'Password must contain at least one uppercase letter.'
  if (!/[a-z]/.test(pw))                   return 'Password must contain at least one lowercase letter.'
  if (!/[0-9]/.test(pw))                   return 'Password must contain at least one digit.'
  return ''
}

export default function ResetPasswordPage() {
  const [searchParams]              = useSearchParams()
  const token                       = searchParams.get('token') ?? ''

  const [password, setPassword]     = useState('')
  const [confirm, setConfirm]       = useState('')
  const [showPw, setShowPw]         = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isLoading, setIsLoading]   = useState(false)
  const [done, setDone]             = useState(false)
  const [errors, setErrors]         = useState<{ password?: string; confirm?: string; form?: string }>({})

  if (!token) {
    return (
      <div className="min-h-screen bg-rd-bg font-dmSans flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-rd-text font-medium mb-2">Invalid reset link</p>
          <p className="text-rd-muted text-sm mb-6">This link is missing a reset token.</p>
          <Link to="/forgot-password" className="text-rd-accent hover:opacity-80 transition-opacity text-sm">
            Request a new link
          </Link>
        </div>
      </div>
    )
  }

  const validate = () => {
    const errs: typeof errors = {}
    const pwError = validatePasswordStrength(password)
    if (pwError) errs.password = pwError
    if (!confirm) {
      errs.confirm = 'Please confirm your password.'
    } else if (password !== confirm) {
      errs.confirm = 'Passwords do not match.'
    }
    return errs
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setErrors({})
    setIsLoading(true)

    try {
      await resetPassword(token, password)
      setDone(true)
    } catch (err) {
      const message = err instanceof Error ? err.message : ''
      if (message === 'invalid_or_expired_token') {
        setErrors({ form: 'This reset link is invalid or has expired. Please request a new one.' })
      } else if (message === 'rate_limit_exceeded') {
        setErrors({ form: 'Too many requests. Please wait a few minutes and try again.' })
      } else {
        setErrors({ form: 'Something went wrong. Please try again.' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-rd-bg font-dmSans relative overflow-hidden">
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px]"
        style={{ background: 'radial-gradient(ellipse, rgba(74,222,128,0.08) 0%, transparent 70%)' }}
      />

      <nav className="relative z-10 px-6 py-4 border-b border-white/[0.07]">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-rd-text no-underline hover:opacity-80 transition-opacity"
        >
          <img
            src={`${import.meta.env.BASE_URL}logo-icon.png`}
            alt="ReputeDesk"
            className="w-8 h-8 object-contain"
          />
          <span className="font-sora font-bold text-lg">ReputeDesk</span>
        </Link>
      </nav>

      <div className="relative z-10 flex justify-center px-4 py-10 sm:py-14">
        <div className="w-full max-w-md">

          <div className="text-center mb-7">
            <h1 className="font-sora font-bold text-2xl sm:text-[1.75rem] text-rd-text mb-2 tracking-tight">
              Choose a new password
            </h1>
            <p className="text-rd-muted text-sm">
              Must be at least 8 characters with uppercase, lowercase, and a digit.
            </p>
          </div>

          <div className="bg-rd-surface border border-white/[0.07] rounded-2xl p-6 sm:p-8">

            {done ? (
              <div className="text-center py-2">
                <div className="mb-4 text-4xl">✅</div>
                <p className="text-rd-text font-medium mb-2">Password updated</p>
                <p className="text-rd-muted text-sm mb-6">
                  Your password has been changed. You can now sign in with your new password.
                </p>
                <Link
                  to="/login"
                  className="reg-btn-primary"
                  style={{ display: 'inline-flex', textDecoration: 'none' }}
                >
                  Sign in
                </Link>
              </div>
            ) : (
              <>
                {errors.form && (
                  <div
                    role="alert"
                    className="mb-5 rounded-lg bg-danger-500/10 border border-danger-500/30 px-4 py-3 text-sm text-danger-500"
                  >
                    {errors.form}
                    {errors.form.includes('invalid or has expired') && (
                      <span>
                        {' '}
                        <Link to="/forgot-password" className="underline">
                          Request a new link
                        </Link>
                      </span>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate className="space-y-5">

                  {/* New password */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-rd-muted mb-1.5">
                      New password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPw ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        aria-required="true"
                        aria-invalid={!!errors.password}
                        aria-describedby={errors.password ? 'pw-error' : undefined}
                        className={`reg-input pr-12${errors.password ? ' !border-danger-500/60' : ''}`}
                        placeholder="New password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        aria-label={showPw ? 'Hide password' : 'Show password'}
                        aria-pressed={showPw}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-rd-dim hover:text-rd-muted transition-colors"
                      >
                        {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p id="pw-error" role="alert" className="mt-1.5 text-sm text-danger-500">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirm password */}
                  <div>
                    <label htmlFor="confirm" className="block text-sm font-medium text-rd-muted mb-1.5">
                      Confirm new password
                    </label>
                    <div className="relative">
                      <input
                        id="confirm"
                        type={showConfirm ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        aria-required="true"
                        aria-invalid={!!errors.confirm}
                        aria-describedby={errors.confirm ? 'confirm-error' : undefined}
                        className={`reg-input pr-12${errors.confirm ? ' !border-danger-500/60' : ''}`}
                        placeholder="Confirm password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        aria-pressed={showConfirm}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-rd-dim hover:text-rd-muted transition-colors"
                      >
                        {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.confirm && (
                      <p id="confirm-error" role="alert" className="mt-1.5 text-sm text-danger-500">
                        {errors.confirm}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    aria-busy={isLoading}
                    className="reg-btn-primary mt-1"
                  >
                    {isLoading ? (
                      <>
                        <svg aria-hidden="true" className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Updating…
                      </>
                    ) : (
                      'Update password'
                    )}
                  </button>

                </form>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
